import { Fragment, useState } from 'react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { leases as leasesApi, vmMetrics as vmMetricsApi } from '@/api/endpoints'
import type { Lease, UsageTimeframe, VmMetrics, VmUsageSeries } from '@/api/types'
import { Gauge } from '@/components/Gauge'
import { share } from '@/components/gauge'
import { Button } from '@/components/ui/button'
import { ChartContainer } from '@/components/ui/chart'
import { diskAllocatedGb } from '@/domain/lease'
import { usePoll } from '@/hooks/usePoll'
import { gib, rate, size, sizeUsage } from '@/ui/format'
import { TIMEFRAMES, timeLabel } from '@/views/nodeUsage'

// 상태 창 — 왼쪽에 CPU·메모리 게이지(할당량이 곧 총량이라 사양 줄은 따로 없다), 가운데에 네트워크의 초당 값과 디스크의 할당·초당 값,
// 오른쪽에 입출력의 그래프. 디스크는 게스트가 쓴 양을 재지 않으므로 고리가 없고, 할당(루트와 데이터 볼륨의 합, 쿼터와 같은 셈)은
// 지금 읽기가 아니라 임대의 값이라 VM 이 꺼져 있거나 아직 놓이지 않아도 선다. 디스크 조작은 여기 없다.
// 지금 값의 원천은 목록과 같은 `GET /vm-metrics`(실행 중이고 닿는 VM 만)이다. 백엔드가 건강 스윕의 기억에서 답하고, 초당 값도 PVE 의
// 두 표본에서 백엔드가 낸다. 그래서 폴링은 그 기억이 바뀌는 주기에 맞춘 고정 간격이다(SSE 는 사용량을 말하지 않는다).
// 그래프의 원천은 PVE 가 VM 마다 갖고 있는 시계열 `GET /leases/{id}/usage`(#257, 1분에 한 점)이고 기간은 노드 화면과 같은 PVE timeframe 이다.
// 점이 1분에 하나 생기므로 60초마다 다시 읽는다. 탭이 숨어 있던 동안도 PVE 가 재고 있었으니 돌아오면 그래프는 차 있다. PVE 가 답하지 않은 구간은 값이 없고 선이 끊긴다 — 0 으로 메우지 않는다.

// 스윕 기본 주기(`clusterhealth.DefaultInterval`)에 맞춘다.
const METRICS_POLL_MS = 5000
const USAGE_POLL_MS = 60000

type IoRow = { time: string; label: string; netIn: number | null; netOut: number | null; diskRead: number | null; diskWrite: number | null }

/** PVE 시계열을 그래프 행으로. 값이 없는 점은 null 그대로다. */
function ioRows(series: VmUsageSeries | null | undefined): IoRow[] {
  if (!series || !Array.isArray(series.points)) return []
  return series.points.map((p) => ({
    time: p.time,
    label: timeLabel(p.time, series.timeframe),
    netIn: p.net_in ?? null,
    netOut: p.net_out ?? null,
    diskRead: p.disk_read ?? null,
    diskWrite: p.disk_write ?? null,
  }))
}

const IO = {
  in: { label: '수신', color: 'var(--chart-1)' },
  out: { label: '송신', color: 'var(--chart-2)' },
  read: { label: '읽기', color: 'var(--chart-1)' },
  write: { label: '쓰기', color: 'var(--chart-2)' },
}

function IoChart({ rows, keys }: { rows: IoRow[]; keys: ['netIn', 'netOut'] | ['diskRead', 'diskWrite'] }) {
  const [ka, kb] = keys[0] === 'netIn' ? (['in', 'out'] as const) : (['read', 'write'] as const)
  return (
    <ChartContainer config={{ [ka]: IO[ka], [kb]: IO[kb] }} className="aspect-auto h-16 w-full">
      <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={48} tick={{ fontSize: 10 }} />
        <YAxis width={44} tickLine={false} axisLine={false} tick={{ fontSize: 10 }} tickFormatter={(v: number) => rate(v)} domain={[0, 'auto']} allowDecimals={false} />
        <Area dataKey={keys[0]} type="monotone" stroke={`var(--color-${ka})`} fill={`var(--color-${ka})`} fillOpacity={0.15} strokeWidth={1.25} connectNulls={false} isAnimationActive={false} dot={false} />
        <Area dataKey={keys[1]} type="monotone" stroke={`var(--color-${kb})`} fill={`var(--color-${kb})`} fillOpacity={0.15} strokeWidth={1.25} connectNulls={false} isAnimationActive={false} dot={false} />
      </AreaChart>
    </ChartContainer>
  )
}

function IoText({ title, rows }: { title: string; rows: { label: string; value: string }[] }) {
  return (
    <div className="grid gap-y-0.5 text-[12px] tabular-nums">
      <span className="font-semibold">{title}</span>
      <div className="grid grid-cols-[2rem_1fr] gap-x-3 pl-3">
        {rows.map((r) => (
          <Fragment key={r.label}>
            <span className="text-muted-foreground">{r.label}</span>
            <span>{r.value}</span>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

export function VmStatusPanel({ lease }: { lease: Lease }) {
  const running = lease.state === 'active' && lease.vm?.state === 'running' && lease.vm.reachable !== false
  const hasVm = Boolean(lease.vm)
  const metrics = usePoll(
    async () => (running ? vmMetricsApi.listFor([lease.id]) : ([] as VmMetrics[])),
    METRICS_POLL_MS,
    [lease.id, running],
  )
  const m = metrics.data?.find((x) => x.lease_id === lease.id)
  const [timeframe, setTimeframe] = useState<UsageTimeframe>('hour')
  // 기계가 정지 중이어도 PVE 는 이력을 갖고 있다. 기계 행이 없으면 물을 것이 없다.
  const usage = usePoll(
    async () => (hasVm ? leasesApi.usage(lease.id, timeframe) : null),
    USAGE_POLL_MS,
    [lease.id, hasVm, timeframe],
  )
  const rows = ioRows(usage.data)

  const memTotal = m?.memory_total_mb ?? lease.memory_mb
  return (
    <div className="grid gap-3 rounded-lg border bg-card px-4 py-3">
      <div className="grid gap-5 lg:grid-cols-[auto_auto_minmax(0,1fr)] lg:items-center lg:gap-8">
        <div className="flex flex-wrap items-center gap-8 max-sm:justify-between max-sm:gap-4">
          <Gauge label="CPU" fraction={m?.cpu ?? null} text={`${lease.cores}코어`} />
          <Gauge label="메모리" fraction={share(m?.memory_used_mb, memTotal)} text={sizeUsage(gib(m?.memory_used_mb), gib(memTotal))} color="var(--chart-2)" />
        </div>
        <div className="grid gap-3 self-center">
          <IoText title="네트워크" rows={[{ label: IO.in.label, value: rate(m?.net_in) }, { label: IO.out.label, value: rate(m?.net_out) }]} />
          <IoText
            title="디스크"
            rows={[
              { label: '할당', value: size(diskAllocatedGb(lease)) },
              { label: IO.read.label, value: rate(m?.disk_read) },
              { label: IO.write.label, value: rate(m?.disk_write) },
            ]}
          />
        </div>
        <div className="grid min-w-0 gap-2">
          <div role="group" aria-label="기간" className="flex justify-end gap-1">
            {TIMEFRAMES.map((t) => (
              <Button key={t.value} size="sm" variant={t.value === timeframe ? 'default' : 'outline'} aria-pressed={t.value === timeframe} className="h-6 px-2 text-[11px]" onClick={() => setTimeframe(t.value)}>
                {t.word}
              </Button>
            ))}
          </div>
          <IoChart rows={rows} keys={['netIn', 'netOut']} />
          <IoChart rows={rows} keys={['diskRead', 'diskWrite']} />
        </div>
      </div>
      {lease.rejected_reason ? (
        <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 text-[12px] max-sm:grid-cols-1 max-sm:gap-0.5">
          <span className="text-muted-foreground">반려 사유</span>
          <span>{lease.rejected_reason}</span>
        </div>
      ) : null}
    </div>
  )
}
